import { useEffect, useState } from 'react';
import { Box, CircularProgress, Modal, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import useDisclaimer from '../../hooks/useDisclaimer';

// Shows a single waiver including its signature image. The signature is not part
// of any list response, so it is fetched per waiver when the modal opens.
export default function WaiverSignatureModal({ waiver, open, onClose }) {
  const theme = useTheme();
  const { getDisclaimer } = useDisclaimer();
  const [signature, setSignature] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const waiverId = waiver?._id;

  useEffect(() => {
    if (!open || !waiverId) return;

    let active = true;
    setSignature(null);
    setIsLoading(true);

    getDisclaimer(waiverId)
      .then(data => {
        if (active) setSignature(data?.signature || null);
      })
      .catch(e => {
        console.error(e);
        toast.error('Fehler beim Laden der Unterschrift');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, waiverId]);

  if (!waiver) return null;

  const children = Array.isArray(waiver.children) ? waiver.children : [];

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: { xs: '90%', sm: 400 },
          maxWidth: 520,
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          border: `2px solid ${theme.palette.divider}`,
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant='h6' component='h2'>
          {waiver.lastName + ', ' + waiver.firstName}
        </Typography>
        <br />
        <Typography variant='body1'>
          <Box component='span' sx={{ fontWeight: 600 }}>
            eMail:
          </Box>{' '}
          {waiver.mail || '—'}
        </Typography>
        {!!children.length && (
          <>
            <br />
            <Typography variant='body1' fontWeight='bold'>
              Kinder
            </Typography>
            <ul style={{ marginBlockStart: 0, marginBlockEnd: 0 }}>
              {children.map((child, index) => (
                <li key={child._id || `${child.lastName}-${child.firstName}-${index}`}>
                  {child.lastName + ', ' + child.firstName}
                </li>
              ))}
            </ul>
          </>
        )}
        <br />
        <Typography variant='body1'>
          <Box component='span' sx={{ fontWeight: 600 }}>
            Zeitpunkt:
          </Box>{' '}
          {waiver.createdAt ? dayjs(waiver.createdAt).format('DD.MM.YYYY HH:mm') + ' Uhr' : '—'}
        </Typography>
        {waiver.bookingNumber && (
          <Typography variant='body1'>
            <Box component='span' sx={{ fontWeight: 600 }}>
              Buchung:
            </Box>{' '}
            {waiver.bookingNumber}
          </Typography>
        )}
        {waiver.lane && (
          <Typography variant='body1'>
            <Box component='span' sx={{ fontWeight: 600 }}>
              Bahn:
            </Box>{' '}
            {waiver.lane}
          </Typography>
        )}
        <br />
        <Typography variant='body1' fontWeight='bold'>
          Unterschrift
        </Typography>
        <br />
        <Paper
          sx={{
            backgroundColor: theme.palette.common.white,
            height: '150px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isLoading && <CircularProgress size={28} />}
            {!isLoading && signature && (
              <img
                src={signature}
                alt='Unterschrift'
                style={{
                  backgroundColor: theme.palette.common.white,
                  maxWidth: '100%',
                  maxHeight: '140px',
                  margin: 'auto',
                  padding: '5px',
                }}
              />
            )}
            {!isLoading && !signature && (
              <Typography variant='body2' sx={{ color: theme.palette.common.black }}>
                Keine Unterschrift vorhanden
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
}
